import React from 'react';
import GameIcon from './GameIcon';

function BadgeRow({ badges, currentValue, color }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {badges.map(badge => {
        const achieved = currentValue >= badge.threshold;
        return (
          <div
            key={badge.threshold}
            className={`rounded-xl p-3 text-center border ${
              achieved
                ? `bg-${color}-50 border-${color}-300`
                : 'bg-gray-50 border-gray-200 opacity-50'
            }`}
          >
            <span className="text-2xl block mb-1">{badge.emoji}</span>
            <p className="text-[10px] font-bold text-gray-800">{badge.name}</p>
            <p className="text-[9px] text-gray-500">{badge.desc}</p>
            {achieved ? (
              <p className={`text-[10px] text-${color}-600 font-bold mt-1`}>✓ Unlocked</p>
            ) : (
              <p className="text-[10px] text-gray-400 mt-1">{currentValue}/{badge.threshold}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardModal({
  totalStars, totalTokensSaved, wardrobe, loginStreak, missionStars, onClose, gameSettings = {}
}) {
  const completedMissions = Object.keys(missionStars || {}).length;

  const bargainBadges = gameSettings.dashboard_bargain_badges || [
    { threshold: 100, name: 'Smart Saver', emoji: '💵', desc: 'Save 100 Tokens' },
    { threshold: 500, name: 'Negotiator', emoji: '💸', desc: 'Save 500 Tokens' },
    { threshold: 1000, name: 'Bargain Pro', emoji: '💎', desc: 'Save 1K Tokens' },
    { threshold: 5000, name: 'Legendary', emoji: '👑', desc: 'Save 5K Tokens' },
  ];

  const collectorBadges = gameSettings.dashboard_collector_badges || [
    { threshold: 10, name: 'First Steps', emoji: '🛍️', desc: 'Own 10 items' },
    { threshold: 25, name: 'Explorer', emoji: '👔', desc: 'Own 25 items' },
    { threshold: 50, name: 'Fashion Lover', emoji: '💃', desc: 'Own 50 items' },
    { threshold: 100, name: 'Wardrobe Master', emoji: '🏪', desc: 'Own 100 items' },
  ];

  const wardrobeCount = Array.isArray(wardrobe) ? wardrobe.length : 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col animate-modal-content">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{gameSettings.dashboard_title || 'Dashboard'}</h3>
            <p className="text-xs text-gray-500">{gameSettings.dashboard_subtitle || 'Stats, Progress & Achievements'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <span className="text-lg block"><GameIcon icon={gameSettings.icon_dashboard_stars || '⭐'} className="inline-block w-5 h-5 object-contain align-middle" /></span>
              <span className="text-lg font-bold text-amber-700">{totalStars || 0}</span>
              <span className="text-[9px] text-gray-500 block">{gameSettings.dashboard_stars || 'Stars'}</span>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <span className="text-lg block"><GameIcon icon={gameSettings.icon_dashboard_saved || '💰'} className="inline-block w-5 h-5 object-contain align-middle" /></span>
              <span className="text-lg font-bold text-green-700">{totalTokensSaved || 0}</span>
              <span className="text-[9px] text-gray-500 block">{gameSettings.dashboard_saved || 'Saved'}</span>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
              <span className="text-lg block"><GameIcon icon={gameSettings.icon_dashboard_items || '👗'} className="inline-block w-5 h-5 object-contain align-middle" /></span>
              <span className="text-lg font-bold text-purple-700">{wardrobeCount}</span>
              <span className="text-[9px] text-gray-500 block">{gameSettings.dashboard_items || 'Items'}</span>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
              <span className="text-lg block"><GameIcon icon={gameSettings.icon_dashboard_streak || '🔥'} className="inline-block w-5 h-5 object-contain align-middle" /></span>
              <span className="text-lg font-bold text-orange-700">{loginStreak || 0}</span>
              <span className="text-[9px] text-gray-500 block">{gameSettings.dashboard_streak || 'Streak'}</span>
            </div>
          </div>

          {/* Missions completed */}
          <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">{gameSettings.dashboard_missions_completed || 'Missions completed'}</span>
            <span className="font-bold text-gray-900">{completedMissions}</span>
          </div>

          {/* Bargaining Badges */}
          <div>
            <h4 className="font-bold text-gray-800 text-sm mb-2 flex items-center gap-1.5">
              <GameIcon icon={gameSettings.icon_dashboard_saved || '💰'} /> {gameSettings.dashboard_bargain_title || 'Bargaining Master'}
            </h4>
            <BadgeRow badges={bargainBadges} currentValue={totalTokensSaved || 0} color="green" />
          </div>

          {/* Collector Badges */}
          <div>
            <h4 className="font-bold text-gray-800 text-sm mb-2 flex items-center gap-1.5">
              <GameIcon icon={gameSettings.icon_dashboard_items || '👗'} /> {gameSettings.dashboard_collector_title || 'Collector'}
            </h4>
            <BadgeRow badges={collectorBadges} currentValue={wardrobeCount} color="purple" />
          </div>

        </div>
      </div>
    </div>
  );
}
